<template>
  <el-dialog
    :visible="true"
    width="1050px"
    :close-on-click-modal="false"
    :before-close="handleClose"
    :fullscreen="true"
  >
    <div slot="title" style="display: flex; align-items: center;">
      <span>{{ title }}</span>
      <el-tag
        v-if="supplierOrder.status && (type === 'update' || type === 'view')"
        size="small"
        :type="getStatus().tag"
        style="margin-left: 5px; margin-right: 5px"
      >
        {{ getStatus().label }}
      </el-tag>
      <el-tag
        v-if="supplierOrder.has_no_more_delivery"
        type="info"
        size="small"
      >
        Ehhez a rendeléshez már nem érkeznek további termékek.
      </el-tag>
    </div>
    <div v-if="type === 'view' && supplierOrder.receipt_number">
      <receipt-link
        :id="supplierOrder.id"
        :label="supplierOrder.receipt_number"
        type="supplierOrder"
        style="margin-bottom: 30px; margin-top: -20px;"
      />
    </div>
    <el-alert
      v-if="supplierOrder.status === 'sent' && type !== 'view'"
      title="Ez a megrendelés már el lett küldve, ezért csak a mennyiségek és a szállítás időpontjának módosítása lehetséges. Módosítás után a bizonylat újragenerálódik."
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 30px; margin-top: -20px;"
    />
    <el-alert
      v-if="supplierOrder.status === 'cancelled' && type !== 'view'"
      title="Ez a megrendelés le lett mondva, ezért nem módosítható."
      type="warning"
      show-icon
      :closable="false"
      style="margin-bottom: 30px; margin-top: -20px;"
    />
    <div v-loading="loading">
      <el-form
        ref="orderForm"
        label-position="top"
        :model="supplierOrder"
        :rules="rules"
      >
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item
              prop="supplier"
              label="Beszállító"
            >
              <el-select
                v-model="supplierOrder.supplier"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="supplierOrder.status !== 'recorded' || type === 'view'"
                @change="onSupplierChange"
              >
                <el-option
                  v-for="supplier in suppliers"
                  :key="supplier.id + 'supplier'"
                  :value="supplier"
                  :label="supplier.company.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item
              prop="customer"
              label="Vevő"
            >
              <el-select
                v-model="supplierOrder.customer"
                style="width: 100%;"
                value-key="id"
                filterable
                :disabled="supplierOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="customer in customers"
                  :key="customer.id + 'supplier'"
                  :value="customer"
                  :label="customer.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item
              prop="storage"
            >
              <div
                slot="label"
                style="display: inline-block;"
              >
                Raktár
                <template v-if="$config('SHOW_STORAGE_TOOLTIPS')">
                  <el-tooltip
                    class="item"
                    effect="dark"
                    :content="`Ebbe a raktárba fog érkezni a szállítmany. ${$config('STORAGE_TOOLTIP_SUFFIX')}`"
                    placement="right"
                  >
                    <i class="el-icon-info"/>
                  </el-tooltip>
                </template>
              </div>
              <el-select
                v-model="supplierOrder.storage"
                style="width: 100%;"
                value-key="id"
                :disabled="supplierOrder.status !== 'recorded' || type === 'view'"
              >
                <el-option
                  v-for="storage in storages"
                  :key="storage.id + 'storage'"
                  :value="storage"
                  :label="storage.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item
              prop="receipt_prefix"
              label="Bizonylat előtag"
            >
              <el-input
                v-model="receiptPrefix"
                disabled
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item
              prop="currency_id"
              label="Deviza"
            >
              <el-select
                v-model="supplierOrder.currency"
                :disabled="supplierOrder.status !== 'recorded' || type === 'view'"
                style="width: 100%;"
                value-key="id"
                filterable
              >
                <el-option
                  v-for="currency in currencies"
                  :key="currency.id + 'currency'"
                  :value="currency"
                  :label="currency.name"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item
              prop="delivery_date"
              label="Szállítás dátuma"
            >
              <el-date-picker
                :key="datePickerKey"
                v-model="supplierOrder.delivery_date"
                type="date"
                :disabled="supplierOrder.status === 'cancelled' || supplierOrder.status === 'done' || supplierOrder.status === 'partly-done' || type === 'view'"
                style="width: 100%;"
                :clearable="false"
                format="yyyy. MM. dd."
                value-format="yyyy-MM-dd"
                :picker-options="{ firstDayOfWeek: 1 }"
                @input="datePickerKey++"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              prop="has_no_more_delivery"
            >
              <el-checkbox v-model="supplierOrder.has_no_more_delivery" :disabled="type === 'view'">
                Nincs további szállítás ehhez a rendeléshez
              </el-checkbox>
            </el-form-item>
          </el-col>
        </el-row>
        <product-items
          ref="productItems"
          :default-items="supplierOrder.order_items"
          :currency="supplierOrder.currency.code"
          :enabled-elements="enabledElements"
          :is-product-select-from-stock="false"
          :enabled-columns="type === 'view' ? ['product', 'productPackType', 'count', 'unitPrice', 'netPrice', 'vat'] :['product', 'productPackType', 'count', 'unitPrice', 'netPrice', 'vat', 'actions']"
          :available-products="products"
          compare-type="completion"
          :compare-items="supplierOrder.inwarded_items && supplierOrder.inwarded_items.length ? supplierOrder.inwarded_items : null"
          :is-vat-zero="supplierOrder.supplier && supplierOrder.supplier.supplier_category && supplierOrder.supplier.supplier_category.value === 'Külföldi szállító'"
          price-type="last_approved_purchase_price"
          :show-row-warning="true"
          @priceChange="onPriceChange"
        />
      </el-form>
      <div class="price-sum">
        <div>Nettó: <strong>{{ supplierOrder.net_price | currency(supplierOrder.currency.code) }}</strong></div>
        <div style="margin-left: 20px;">
          Bruttó: <strong>{{ supplierOrder.gross_price | currency(supplierOrder.currency.code) }}</strong>
        </div>
      </div>
    </div>
    <div
      v-if="type !== 'view'"
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        v-if="supplierOrder.status !== 'recorded' && supplierOrder.status !== 'cancelled' && supplierOrder.receipt_file"
        @click="openReceipt"
      >
        Bizonylat nyomtatása
      </el-button>
      <el-button
        v-if="supplierOrder.status === 'recorded' && type === 'update'"
        :loading="sendButtonLoading"
        @click="send"
      >
        Rendelés elküldése
      </el-button>
      <el-button
        v-if="supplierOrder.status === 'sent'"
        type="warning"
        :loading="cancelButtonLoading"
        @click="cancel"
      >
        Rendelés lemondása
      </el-button>
      <el-button
        v-if="supplierOrder.status === 'cancelled'"
        type="success"
        :loading="restoreButtonLoading"
        @click="restore"
      >
        Lemondás visszavonása
      </el-button>
      <el-button
        type="primary"
        :loading="buttonLoading"
        :disabled="supplierOrder.status === 'cancelled'"
        @click="store"
      >
        {{ buttonText }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import ProductItems from '../../utils/ProductItems/index';
import ReceiptLink from '../../utils/ReceiptLink';

export default {
  components: {
    ProductItems,
    ReceiptLink,
  },
  props: ['type', 'supplierOrderId'],
  data() {
    return {
      loading: false,
      buttonLoading: false,
      cancelButtonLoading: false,
      restoreButtonLoading: false,
      sendButtonLoading: false,
      datePickerKey: 0,
      receiptPrefix: '',
      products: null,
      supplierOrder: {
        status: 'recorded',
        net_price: 0,
        gross_price: 0,
        order_items: [],
        storage: {},
        customer: {},
        currency: {},
        has_no_more_delivery: false,
      },
      rules: {
        customer: [
          {
            required: true,
            message: 'Vevő kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        supplier: [
          {
            required: true,
            message: 'Beszállító kiválasztása kötelező',
            trigger: 'change'
          }
        ],
        storage: [
          {
            required: true,
            message: 'Raktár kiválasztása kötelező',
            trigger: 'change'
          }
        ],
      },
    };
  },
  computed: {
    title() {
      if (this.type === 'add') {
        return 'Új beszállítói megrendelés';
      } else if (this.type === 'update') {
        return 'Beszállítói megrendelés módosítása';
      } else if (this.type === 'view') {
        return 'Beszállítói megrendelés megtekintése';
      } else {
        return '';
      }
    },
    buttonText() {
      if (this.type === 'add') {
        return 'Hozzáadása';
      } else if (this.type === 'update') {
        return 'Módosítás';
      } else {
        return '';
      }
    },
    enabledElements() {
      if(this.type === 'view') {
        return  [];
      }
      switch (this.supplierOrder.status) {
        case 'recorded':
          return ['addButton', 'deleteButton', 'product', 'pack', 'count', 'netPrice', 'vat', 'unitPrice'];
        case 'sent':
          return ['count'];
        case 'cancelled':
          return [];
        case 'done':
          return [];
        default:
          return [];
      }
    },
    customers() {
      return this.$store.getters['ownCompany/ownCompanies'];
    },
    suppliers() {
      return this.$store.getters['partner/suppliers'];
    },
    storages() {
      return this.$store.getters['storage/ownStorages'];
    },
    currencies() {
      return this.$store.getters['currency/currencies'];
    },
  },
  async mounted() {
    this.loading = true;
    const requests = [];
    if(!this.customers || !this.customers.length){
      requests.push(this.$store.dispatch('ownCompany/fetchAll', ''));
    }
    if(!this.suppliers || !this.suppliers.length){
      requests.push(this.$store.dispatch('partner/fetchSuppliers', ''));
    }
    if(!this.storages || !this.storages.length){
      requests.push(this.$store.dispatch('storage/fetchAll', ''));
    }
    if(!this.currencies || !this.currencies.length){
      requests.push(this.$store.dispatch('currency/fetchAll'));
    }
    if(!this.$store.getters['vat/vats'] || !this.$store.getters['currency/currencies'].length){
      requests.push(this.$store.dispatch('vat/fetchAll'));
    }
    await Promise.all(requests);
    this.receiptPrefix = (await axios.get('api/receipt-prefixes/supplierOrder')).full_prefix;
    if (this.supplierOrderId) {
      this.supplierOrder = await this.$store.dispatch('supplierOrder/fetch', this.supplierOrderId);
      if(this.type === 'add') {
        this.supplierOrder.status = 'recorded';
        this.supplierOrder.receipt_number = null;
        this.supplierOrder.order_items = this.supplierOrder.order_items.map(item => {
          delete item.id;
          return item;
        });
      }
      if(this.type !== 'add') {
        this.supplierOrder.inwarded_items = await axios.get('api/supplier-orders/inwarded-items/' + this.supplierOrder.id);
      }
      this.products = await this.$store.dispatch('product/fetchBySupplierId', this.supplierOrder.supplier.id);
    } else {
      this.supplierOrder.customer = this.customers[0];
      this.supplierOrder.currency = this.currencies[0];
      this.supplierOrder.storage = this.storages[0];
      this.supplierOrder.delivery_date = moment()
        .add(3, 'days')
        .format('Y-MM-DD');
    }
    this.loading = false;
  },
  methods: {
    async onSupplierChange() {
      this.loading = true;
      this.supplierOrder.order_items = [];
      this.products = await this.$store.dispatch('product/fetchBySupplierId', this.supplierOrder.supplier.id);
      this.loading = false;
    },
    onPriceChange({ netPrice, grossPrice }) {
      this.supplierOrder.net_price = netPrice;
      this.supplierOrder.gross_price = grossPrice;
    },
    handleClose() {
      if(this.type === 'view') {
        return this.$router.replace('/processes/supplier-orders');
      }
      this.$confirm('Biztosan elveted a módosításokat?', {
        confirmButtonText: 'Igen',
        type: 'warning',
      })
        .then(() => {
          this.$router.replace('/processes/supplier-orders');
        })
        .catch(() => {
        });
    },
    async cancel() {
      this.cancelButtonLoading = true;
      await this.$store.dispatch('supplierOrder/cancel', this.supplierOrder.id);
      this.loading = true;
      await this.$store.dispatch('supplierOrder/fetchAll');
      this.supplierOrder = await this.$store.dispatch('supplierOrder/fetch', this.supplierOrderId);
      this.loading = false;
      this.cancelButtonLoading = false;
    },
    async restore() {
      this.restoreButtonLoading = true;
      await this.$store.dispatch('supplierOrder/undoCancel', this.supplierOrder.id);
      this.loading = true;
      await this.$store.dispatch('supplierOrder/fetchAll');
      this.supplierOrder = await this.$store.dispatch('supplierOrder/fetch', this.supplierOrderId);
      this.loading = false;
      this.restoreButtonLoading = false;
    },
    async send() {
      this.supplierOrder.order_items = this.$refs['productItems'].getItems();
      if (!this.supplierOrder.order_items.length) {
        return this.$messageWrapper.warning('Minimum 1 termék hozzáadása kötelező.');
      } else if (!this.$refs['productItems'].validateProducts()) {
        return this.$messageWrapper.warning('Termékadatok hibásan vannak kitöltve.');
      }
      this.sendButtonLoading = true;
      await this.$store.dispatch('supplierOrder/update', this.supplierOrder);
      await this.$store.dispatch('supplierOrder/send', this.supplierOrder.id);
      this.loading = true;
      await this.$store.dispatch('supplierOrder/fetchAll');
      this.supplierOrder = await this.$store.dispatch('supplierOrder/fetch', this.supplierOrderId);
      this.loading = false;
      this.sendButtonLoading = false;
    },
    async store() {
      this.$refs.orderForm.validate(async valid => {
        if (valid) {
          this.supplierOrder.order_items = this.$refs['productItems'].getItems();
          if (!this.supplierOrder.order_items.length) {
            return this.$messageWrapper.warning('Minimum 1 termék hozzáadása kötelező.');
          } else if (!this.$refs['productItems'].validateProducts()) {
            return this.$messageWrapper.warning('Termékadatok hibásan vannak kitöltve.');
          }
          this.buttonLoading = true;
          this.supplierOrder.order_items = this.supplierOrder.order_items.map(item => ({
            ...item,
            supplier: this.supplierOrder.supplier,
          }));
          try {
            if (this.type === 'add') {
              await this.$store.dispatch('supplierOrder/create', this.supplierOrder);
            } else if (this.type === 'update') {
              await this.$store.dispatch('supplierOrder/update', this.supplierOrder);
            }
            this.$store.dispatch('supplierOrder/fetchAll');
            this.buttonLoading = false;
            this.$router.replace('/processes/supplier-orders');
          } catch (e) {
          }
          this.buttonLoading = false;
        }
      });
    },
    openReceipt() {
      if (this.supplierOrder.receipt_file && this.supplierOrder.receipt_file.path) {
        window.open(this.supplierOrder.receipt_file.path);
      }
    },
    getStatus() {
      switch (this.supplierOrder.status) {
        case 'recorded':
          return {
            label: 'Rögzítve',
            tag: 'info',
          };
        case 'cancelled':
          return {
            label: 'Lemondva',
            tag: 'danger',
          };
        case 'done':
          return {
            label: 'Bevételezett',
            tag: 'success',
          };
        case 'partly-done':
          return {
            label: 'Részben bevételezett',
            tag: 'warning',
          };
        case 'sent':
          return {
            label: 'Elküldve',
            tag: 'primary',
          };
      }
    },
  },
};
</script>

<style lang="scss">
  .price-sum {
    margin-top: 20px;
    text-align: right;
    font-size: 18px;

    > div {
      display: inline-block;
    }
  }
</style>
